import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { CheckCircleIcon } from '../../../../components/ui/Icons';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent 
} from '../../../../components/ui';

const ApprovalProgressOverview = ({ selectedRecord }) => {
  const { theme } = useTheme();

  if (!selectedRecord.steps || selectedRecord.steps.length === 0) {
    return null;
  }

  // Helper function to check if a step is completed
  const isStepCompleted = (step) => {
    // A step is completed if it has approvedAt, reviewedAt, or rejectedAt
    return !!(step.approvedAt || step.reviewedAt || step.rejectedAt);
  };

  // Helper function to check if a step is pending
  const isStepPending = (step) => {
    // A step is pending if it has no action taken yet
    return !step.approvedAt && !step.reviewedAt && !step.rejectedAt;
  };

  // Helper function to check if a step is approved (not rejected)
  const isStepApproved = (step) => {
    // For approval steps, check approvedAt
    // For review steps, check reviewedAt (they can proceed after review)
    if (step.stepType === 'review') {
      return !!step.reviewedAt && !step.rejectedAt;
    }
    return !!step.approvedAt && !step.rejectedAt;
  };

  // Calculate progress based on approval type
  const calculateProgress = (selectedRecord) => {
    const { steps, approvalType } = selectedRecord;
    
    if (!steps || steps.length === 0) {
      return { completed: 0, total: 0, percentage: 0, currentStep: null, isComplete: false };
    }
    
    // Filter to only required steps for progress calculation
    const requiredSteps = steps.filter(step => step.isRequired !== false);
    const allRequiredSteps = requiredSteps.length > 0 ? requiredSteps : steps;
    
    // Get completed steps (have approvedAt, reviewedAt, or rejectedAt)
    const completedSteps = allRequiredSteps.filter(isStepCompleted);
    
    let completedCount = 0;
    let totalRequired = allRequiredSteps.length;
    let currentStep = null;
    let isComplete = false;
    
    // Check if any step is rejected (rejection usually fails the whole request)
    const hasRejectedStep = allRequiredSteps.some(step => step.rejectedAt);
    
    if (hasRejectedStep) {
      // If any required step is rejected, the approval is considered failed
      return {
        completed: completedSteps.length,
        total: totalRequired,
        percentage: 0,
        currentStep: null,
        isComplete: false,
        hasRejected: true
      };
    }
    
    switch (approvalType) {
      case 'single':
        // Single approver: 1 of 1 required step
        completedCount = allRequiredSteps.filter(isStepApproved).length;
        isComplete = completedCount >= 1;
        currentStep = allRequiredSteps.find(isStepPending);
        break;
        
      case 'sequential':
        // Sequential: Must complete in order
        let sequentialCompleted = 0;
        for (let i = 0; i < allRequiredSteps.length; i++) {
          const step = allRequiredSteps[i];
          if (isStepApproved(step)) {
            sequentialCompleted++;
          } else {
            break; // Stop at first non-approved step
          }
        }
        completedCount = sequentialCompleted;
        isComplete = completedCount >= allRequiredSteps.length;
        currentStep = allRequiredSteps.find(isStepPending);
        break;
        
      case 'parallel':
        // Parallel: All must approve (or review, then approve)
        completedCount = allRequiredSteps.filter(isStepApproved).length;
        isComplete = completedCount >= allRequiredSteps.length;
        currentStep = allRequiredSteps.find(isStepPending);
        break;
        
      case 'any_one':
        // Any one: First approval completes it
        const firstApproved = allRequiredSteps.find(isStepApproved);
        completedCount = firstApproved ? 1 : 0;
        totalRequired = 1;
        isComplete = completedCount >= 1;
        currentStep = firstApproved ? null : allRequiredSteps.find(isStepPending);
        break;
        
      case 'review_approval':
        // Review and approval: All review steps must be reviewed, then all approval steps must be approved
        const reviewSteps = allRequiredSteps.filter(s => s.stepType === 'review');
        const approvalSteps = allRequiredSteps.filter(s => s.stepType === 'approval');
        
        const reviewedCount = reviewSteps.filter(s => s.reviewedAt).length;
        const approvedCount = approvalSteps.filter(s => s.approvedAt).length;
        
        const allReviewsDone = reviewSteps.length === 0 || reviewedCount >= reviewSteps.length;
        const allApprovalsDone = approvalSteps.length === 0 || approvedCount >= approvalSteps.length;
        
        // For progress, count review as half completion
        if (reviewSteps.length > 0 && approvalSteps.length > 0) {
          completedCount = reviewedCount + approvedCount;
          totalRequired = reviewSteps.length + approvalSteps.length;
          isComplete = allReviewsDone && allApprovalsDone;
        } else {
          completedCount = reviewedCount + approvedCount;
          totalRequired = allRequiredSteps.length;
          isComplete = completedCount >= totalRequired;
        }
        
        // Find first pending step (prioritize review steps)
        currentStep = reviewSteps.find(isStepPending) || approvalSteps.find(isStepPending);
        break;
        
      default:
        // Default to parallel behavior
        completedCount = allRequiredSteps.filter(isStepApproved).length;
        isComplete = completedCount >= allRequiredSteps.length;
        currentStep = allRequiredSteps.find(isStepPending);
    }
    
    return {
      completed: completedCount,
      total: totalRequired,
      percentage: totalRequired > 0 ? (completedCount / totalRequired) * 100 : 0,
      currentStep,
      isComplete,
      hasRejected: false
    };
  };

  const progress = calculateProgress(selectedRecord);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center">
          <CheckCircleIcon className="w-4 h-4 mr-2" />
          Progress Overview
        </CardTitle>
        <CardDescription>
          Overall progress and current step information
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Approval Type Badge */}
          <div className="flex items-center justify-between py-2">
            <span className={cn("text-sm font-medium", theme.text.secondary)}>Approval Type</span>
            <span className={cn("text-xs px-2 py-1 rounded-full", 
              selectedRecord.approvalType === 'single' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
              selectedRecord.approvalType === 'sequential' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
              selectedRecord.approvalType === 'parallel' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
              selectedRecord.approvalType === 'any_one' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
              'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
            )}>
              {selectedRecord.approvalType === 'single' ? 'Single' :
               selectedRecord.approvalType === 'sequential' ? 'Sequential' :
               selectedRecord.approvalType === 'parallel' ? 'Parallel' :
               selectedRecord.approvalType === 'any_one' ? 'Any One' :
               'Unknown'}
            </span>
          </div>

          {/* Progress Counter */}
          <div className="flex items-center justify-between py-2">
            <span className={cn("text-sm font-medium", theme.text.secondary)}>Progress</span>
            <span className={cn("text-sm font-medium", 
              progress.hasRejected ? theme.text.destructive :
              progress.isComplete ? theme.text.success : theme.text.primary
            )}>
              {progress.completed} of {progress.total} completed
              {progress.isComplete && ' ✅'}
              {progress.hasRejected && ' ❌ Rejected'}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={cn("h-2 rounded-full transition-all duration-300",
                progress.hasRejected ? 'bg-red-600' :
                progress.isComplete ? 'bg-green-600' : 'bg-blue-600'
              )}
              style={{ width: `${Math.max(0, Math.min(100, progress.percentage))}%` }}
            ></div>
          </div>

          {/* Current Step Information */}
          {progress.currentStep && (
            <div className="space-y-2">
              <div className="flex items-center justify-between py-2">
                <span className={cn("text-sm font-medium", theme.text.secondary)}>Current Step</span>
                <span className={cn("text-sm font-medium", theme.text.primary)}>
                  {progress.currentStep.stepName || `Step ${progress.currentStep.stepNumber || selectedRecord.steps.indexOf(progress.currentStep) + 1}`}
                </span>
              </div>
              {progress.currentStep.assignedApprover && (
                <div className="flex items-center justify-between py-2">
                  <span className={cn("text-sm font-medium", theme.text.secondary)}>Assigned To</span>
                  <span className={cn("text-sm font-medium", theme.text.primary)}>
                    {progress.currentStep.assignedApprover.userDisplayName || 
                     progress.currentStep.assignedApprover.userFirstName + ' ' + progress.currentStep.assignedApprover.userLastName ||
                     progress.currentStep.assignedApprover.userEmail || 'Unknown User'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Completion Status */}
          {progress.isComplete && (
            <div className="flex items-center justify-center py-2">
              <span className={cn("text-sm font-semibold", theme.text.success)}>
                🎉 Approval Process Complete!
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ApprovalProgressOverview;
